<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? getDynamicTitle() : 'Deleted Education Details'"
              :subtitle="isDeletePage() != true ? getDynamicSubtitle() : 'Restore or permanently delete education details'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <!-- Back to Doctor button (only show in doctor context) -->
                    <v-btn
                      v-if="$route.name === 'DoctorEducationDetailsPage'"
                      outlined
                      color="grey darken-1"
                      small
                      @click="goBackToDoctor"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Doctor
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      :to="getCreateRoute()"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Education
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Education Details</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row class="mb-2">
                <!-- Search Input -->
                <v-col cols="12" md="4" class="py-1">
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search education details..."
                    solo
                    rounded
                    dense
                    hide-details
                    clearable
                    @input="searchEducationDetails"
                    class="text-body-2"
                  ></v-text-field>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="2" class="py-1">
                  <v-select
                    v-model="filters.user_id"
                    :items="usersList"
                    item-value="id"
                    item-text="display_name"
                    label="User"
                    solo
                    rounded
                    dense
                    hide-details
                    clearable
                    @change="applyFilters"
                    class="text-body-2"
                  ></v-select>
                </v-col>

                <!-- Verification Status Filter -->
                <v-col cols="12" md="2" class="py-1">
                  <v-select
                    v-model="filters.status"
                    :items="statusOptions"
                    item-value="value"
                    item-text="text"
                    label="Verification Status"
                    solo
                    rounded
                    dense
                    hide-details
                    clearable
                    @change="applyFilters"
                    class="text-body-2"
                  ></v-select>
                </v-col>

                <!-- Active Status Filter -->
                <v-col cols="12" md="2" class="py-1">
                  <v-select
                    v-model="filters.is_active"
                    :items="activeOptions"
                    item-value="value"
                    item-text="text"
                    label="Status"
                    solo
                    rounded
                    dense
                    hide-details
                    clearable
                    @change="applyFilters"
                    class="text-body-2"
                  ></v-select>
                </v-col>

                <!-- Actions -->
                <v-col cols="12" md="2" class="py-1">
                  <div class="d-flex align-center">
                    <v-btn
                      color="success"
                      outlined
                      small
                      @click="exportToExcel"
                      :loading="exportLoading"
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-file-excel</v-icon>
                      Export
                    </v-btn>
                    <v-btn
                      :color="isDeletePage() ? 'primary' : 'error'"
                      outlined
                      small
                      @click="toggleDeletePage"
                    >
                      <v-icon left x-small>
                        {{ isDeletePage() ? "mdi-arrow-left" : "mdi-delete" }}
                      </v-icon>
                      {{ isDeletePage() ? "Back" : "Trash" }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="educationDetails"
                :items-per-page="rowsPerPage"
                :page="page"
                :server-items-length="totalItems"
                :loading="loading"
                fixed-header
                height="285"
                class="elevation-0"
                @update:page="handlePageChange"
                @update:items-per-page="handleRowsPerPageChange"
              >
                <!-- Top Slot for Custom Header -->
                <template v-slot:top>
                  <v-toolbar flat class="px-4 py-2">
                    <v-row
                      align="center"
                      justify="space-between"
                      class="w-100 ma-0"
                    >
                      <v-col cols="6" class="pa-0">
                        <div class="d-flex align-center">
                          <v-icon class="mr-2" color="primary"
                            >mdi-school</v-icon
                          >
                          <span class="text-h6 font-weight-bold">
                            Education Details ({{ totalItems }})
                          </span>
                        </div>
                      </v-col>
                      <v-col cols="6" class="pa-0">
                        <div class="d-flex align-center justify-end">
                          <v-select
                            v-model="rowsPerPage"
                            :items="rowsPerPageOptions"
                            label="Rows per page"
                            dense
                            outlined
                            hide-details
                            class="rows-per-page-select"
                            style="max-width: 120px"
                            @change="handleRowsPerPageChange"
                          ></v-select>
                        </div>
                      </v-col>
                    </v-row>
                  </v-toolbar>
                  <v-divider></v-divider>
                </template>

                <!-- User Column -->
                <template v-slot:[`item.user`]="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar size="32" class="mr-3">
                      <v-icon>mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">
                        {{
                          item.user
                            ? item.user.first_name + " " + item.user.last_name
                            : "N/A"
                        }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user ? item.user.email : "" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Qualification Column -->
                <template v-slot:[`item.qualification`]="{ item }">
                  <div>
                    <div class="font-weight-medium">
                      {{ item.qualification }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.specialization }}
                    </div>
                  </div>
                </template>

                <!-- University Column -->
                <template v-slot:[`item.university`]="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.university }}</div>
                    <div class="text-caption grey--text">
                      {{ item.year_of_passing }}
                    </div>
                  </div>
                </template>

                <!-- Verification Status Column -->
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    x-small
                    class="ma-1"
                  >
                    <v-icon left x-small>
                      {{ getStatusIcon(item.status) }}
                    </v-icon>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>

                <!-- Verified By Column -->
                <template v-slot:[`item.verified_by`]="{ item }">
                  <div v-if="item.verified_by">
                    <div class="font-weight-medium text-caption">
                      {{
                        item.verified_by.first_name +
                        " " +
                        item.verified_by.last_name
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.verified_at ? formatDate(item.verified_at) : "" }}
                    </div>
                  </div>
                  <span v-else class="text-caption grey--text"
                    >Not verified</span
                  >
                </template>

                <!-- Certificate Column -->
                <template v-slot:[`item.certificate`]="{ item }">
                  <v-chip
                    :color="item.certificate_file_path ? 'info' : 'grey'"
                    dark
                    x-small
                  >
                    <v-icon left x-small>
                      {{
                        item.certificate_file_path
                          ? "mdi-file-document"
                          : "mdi-file-document-outline"
                      }}
                    </v-icon>
                    {{
                      item.certificate_file_path ? "Available" : "Not Available"
                    }}
                  </v-chip>
                </template>

                <!-- Active Status Column -->
                <template v-slot:[`item.is_active`]="{ item }">
                  <status-chip x-small :color="item.is_active ? 'success' : 'error'" :label="String(item.is_active ? 'Active' : 'Inactive')" />
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex align-center">
                    <!-- View/Edit Button -->
                    <v-btn
                      color="primary"
                      icon
                      x-small
                      :to="`/education_details/${item.id}`"
                      class="mr-1"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>

                    <!-- Approve/Reject Button (Admin only) -->
                    <v-btn
                      v-if="canVerify && item.status === 0"
                      color="success"
                      icon
                      x-small
                      @click="approveEducation(item)"
                      class="mr-1"
                    >
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canVerify && item.status === 0"
                      color="error"
                      icon
                      x-small
                      @click="rejectEducation(item)"
                      class="mr-1"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>

                    <!-- Delete/Restore Button -->
                    <v-btn
                      :color="isDeletePage() ? 'success' : 'error'"
                      icon
                      x-small
                      @click="
                        isDeletePage() ? restoreItem(item) : deleteItem(item)
                      "
                    >
                      <v-icon>
                        {{ isDeletePage() ? "mdi-restore" : "mdi-delete" }}
                      </v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- No Data Slot -->
                <template v-slot:no-data>
                  <empty-state icon="mdi-school-outline" title="No education details found" :message="isDeletePage() ? &quot;No deleted education details&quot; : &quot;Start by adding some education details&quot;" />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmationDialog
      v-if="itemToDelete"
      v-model="showDeleteDialog"
      :item="itemToDelete"
      item-name-field="qualification"
      :title="isDeletePage() ? 'Confirm Permanent Delete' : 'Confirm Delete'"
      :message="
        isDeletePage()
          ? 'Are you sure you want to permanently delete this education detail?'
          : 'Are you sure you want to move this education detail to trash?'
      "
      :confirm-text="isDeletePage() ? 'Delete Permanently' : 'Move to Trash'"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <!-- Restore Confirmation Dialog -->
    <v-dialog v-model="showRestoreDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Restore</v-card-title>
        <v-card-text>
          Are you sure you want to restore this education detail?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="cancelRestore"
            >Cancel</v-btn
          >
          <v-btn color="success" @click="confirmRestore">Restore</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "EducationDetailsIndex",
  components: {
    DeleteConfirmationDialog,
  },

  data() {
    return {
      // Data table configuration
      educationDetails: [],
      loading: false,
      exportLoading: false,
      page: 1,
      totalItems: 0,
      rowsPerPage: 15,
      rowsPerPageOptions: [15, 25, 50, 100],

      // Search and filters
      search: "",
      filters: {
        user_id: null,
        status: null,
        is_active: null,
        show_deleted: 0,
      },

      // Options for filters
      usersList: [],
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "Verified", value: 1 },
        { text: "Rejected", value: 2 },
      ],
      activeOptions: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],

      // Dialog states
      showDeleteDialog: false,
      showRestoreDialog: false,
      itemToDelete: null,
      itemToRestore: null,

      // Table headers
      headers: [
        { text: "User", value: "user", sortable: false, width: "200px" },
        {
          text: "Qualification",
          value: "qualification",
          sortable: true,
          width: "180px",
        },
        {
          text: "University/Year",
          value: "university",
          sortable: true,
          width: "200px",
        },
        {
          text: "Verification",
          value: "status",
          sortable: true,
          width: "120px",
          align: "center",
        },
        {
          text: "Verified By",
          value: "verified_by",
          sortable: false,
          width: "150px",
        },
        {
          text: "Certificate",
          value: "certificate",
          sortable: false,
          width: "120px",
          align: "center",
        },
        {
          text: "Status",
          value: "is_active",
          sortable: true,
          width: "100px",
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "150px",
          align: "center",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["user", "role"]),

    hasActiveFilters() {
      return (
        this.search ||
        this.filters.user_id ||
        this.filters.status !== null ||
        this.filters.is_active !== null
      );
    },

    canVerify() {
      return this.role && ["SUPER ADMIN", "ADMIN"].includes(this.role.name);
    },
  },

  mounted() {
    this.loadEducationDetails();
    this.loadUsers();
  },

  methods: {
    getCreateRoute() {
      // Check if we're on a doctor-specific education details page
      if (
        this.$route.name === "DoctorEducationDetailsPage" &&
        this.$route.params.doctorId
      ) {
        return `/doctors/${this.$route.params.doctorId}/education-details/create`;
      }
      return "/education_details/create";
    },

    getDynamicTitle() {
      if (this.$route.name === 'DoctorEducationDetailsPage') {
        return "Doctor Education Details";
      }
      return "Education Details Management";
    },

    getDynamicSubtitle() {
      if (this.$route.name === 'DoctorEducationDetailsPage') {
        return "View and manage this doctor's educational qualifications";
      }
      return "Manage and verify educational qualifications";
    },

    goBackToDoctor() {
      // Use browser back navigation for better UX
      this.$router.back();
    },

    async loadEducationDetails() {
      try {
        this.loading = true;
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          search_keyword: this.search,
          show_deleted: this.filters.show_deleted,
          ...this.filters,
        };

        // If this is accessed via doctor route, filter by specific doctor
        if (this.$route.name === 'DoctorEducationDetailsPage' && this.$route.params.doctorId) {
          params.user_id = this.decryptIt(this.$route.params.doctorId);
        }

        // Remove null values
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/education_details", { params });

        if (response.data.success) {
          this.educationDetails = response.data.data;
          this.totalItems = response.data.count;
        } else {
          this.$store.dispatch(
            "errors/setError",
            response.data.message || "Failed to load education details"
          );
        }
      } catch (error) {
        console.error("Error loading education details:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error loading education details"
        );
      } finally {
        this.loading = false;
      }
    },

    async loadUsers() {
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: -1 },
        });

        if (response.data.success) {
          this.usersList = response.data.data.map((user) => ({
            id: user.id,
            display_name: `${user.first_name} ${user.last_name}`,
          }));
        }
      } catch (error) {
        console.error("Error loading users:", error);
      }
    },

    searchEducationDetails() {
      this.page = 1;
      this.loadEducationDetails();
    },

    applyFilters() {
      this.page = 1;
      this.loadEducationDetails();
    },

    handlePageChange(newPage) {
      this.page = newPage;
      this.loadEducationDetails();
    },

    handleRowsPerPageChange(newRowsPerPage) {
      this.rowsPerPage = newRowsPerPage;
      this.page = 1;
      this.loadEducationDetails();
    },

    clearAllFilters() {
      this.search = "";
      this.filters = {
        user_id: null,
        status: null,
        is_active: null,
        show_deleted: this.filters.show_deleted,
      };
      this.page = 1;
      this.loadEducationDetails();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.search) count++;
      if (this.filters.user_id) count++;
      if (this.filters.status !== null) count++;
      if (this.filters.is_active !== null) count++;
      return count;
    },

    isDeletePage() {
      return this.filters.show_deleted === 1;
    },

    toggleDeletePage() {
      this.filters.show_deleted = this.filters.show_deleted ? 0 : 1;
      this.page = 1;
      this.loadEducationDetails();
    },

    async approveEducation(item) {
      try {
        const response = await axios.post(
          `/education_details/approve/${this.encryptIt(item.id)}`
        );

        if (response.data.success) {
          this.$store.dispatch("success/setSuccess", "Education detail approved successfully");
          this.loadEducationDetails();
        } else {
          this.$store.dispatch("errors/setError", response.data.message);
        }
      } catch (error) {
        console.error("Error approving education detail:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error approving education detail"
        );
      }
    },

    async rejectEducation(item) {
      const reason = prompt("Please provide a reason for rejection:");
      if (!reason) return;

      try {
        const response = await axios.post(
          `/education_details/reject/${this.encryptIt(item.id)}`,
          { rejection_reason: reason }
        );

        if (response.data.success) {
          this.$store.dispatch("success/setSuccess", "Education detail rejected successfully");
          this.loadEducationDetails();
        } else {
          this.$store.dispatch("errors/setError", response.data.message);
        }
      } catch (error) {
        console.error("Error rejecting education detail:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error rejecting education detail"
        );
      }
    },

    deleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },

    restoreItem(item) {
      this.itemToRestore = item;
      this.showRestoreDialog = true;
    },

    async confirmDelete() {
      try {
        if (!this.itemToDelete) return;

        const endpoint = this.isDeletePage()
          ? `/education_details/${this.itemToDelete.id}`
          : `/education_details/delete/${this.itemToDelete.id}`;

        const method = this.isDeletePage() ? "delete" : "post";
        const response = await axios[method](endpoint);

        if (response.data.success) {
          this.$store.dispatch("success/setSuccess", response.data.message);
          this.loadEducationDetails();
        } else {
          this.$store.dispatch("errors/setError", response.data.message);
        }
      } catch (error) {
        console.error("Error deleting education detail:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error deleting education detail"
        );
      } finally {
        this.cancelDelete();
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.itemToDelete = null;
    },

    async confirmRestore() {
      try {
        if (!this.itemToRestore) return;

        const response = await axios.post(
          `/education_details/restore/${this.itemToRestore.id}`
        );

        if (response.data.success) {
          this.$store.dispatch("success/setSuccess", response.data.message);
          this.loadEducationDetails();
        } else {
          this.$store.dispatch("errors/setError", response.data.message);
        }
      } catch (error) {
        console.error("Error restoring education detail:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error restoring education detail"
        );
      } finally {
        this.cancelRestore();
      }
    },

    cancelRestore() {
      this.showRestoreDialog = false;
      this.itemToRestore = null;
    },

    async exportToExcel() {
      try {
        this.exportLoading = true;
        const params = {
          search_keyword: this.search,
          show_deleted: this.filters.show_deleted,
          ...this.filters,
        };

        // Remove null values
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/education_details/excel", {
          params,
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `education_details_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$store.dispatch(
          "success/setSuccess",
          "Education details exported successfully"
        );
      } catch (error) {
        console.error("Error exporting education details:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error exporting education details"
        );
      } finally {
        this.exportLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    },

    getStatusColor(status) {
      switch (Number(status)) {
        case 0:
          return "warning"; // Pending
        case 1:
          return "success"; // Verified
        case 2:
          return "error"; // Rejected
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 0:
          return "Pending";
        case 1:
          return "Verified";
        case 2:
          return "Rejected";
        default:
          return "Unknown";
      }
    },

    getStatusIcon(status) {
      switch (Number(status)) {
        case 0:
          return "mdi-clock"; // Pending
        case 1:
          return "mdi-check-circle"; // Verified
        case 2:
          return "mdi-close-circle"; // Rejected
        default:
          return "mdi-help-circle";
      }
    },
  },
};
</script>

<style scoped>
.rows-per-page-select .v-input__control .v-input__slot {
  min-height: 32px !important;
}

.rows-per-page-select .v-select__selection {
  font-size: 0.875rem;
}
</style>